import React from 'react';

import { Box, Tooltip, Typography, createStyles, makeStyles, Theme, useTheme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

import { OnboardingStatusBase, WorkerOnboarding } from '../types';
import { getModuleCompletionPercent, getOnboardingModuleKeyByName } from '../utils/onboardingStatus';
import { monochrome, storiesDarkModeColor } from '../theme/styles';

interface Props {
  // The row's module membership — a single name, a comma-separated string or an array.
  // Accepts every spelling the API uses ('Payroll', 'pay', 'Time&Attendance', ...).
  modules?: string | string[] | null;
  onboarding?: WorkerOnboarding | null;
  // Tooltip line for a module with no computed status; invites for people
  // with no Vyce account pass "Not started"
  emptyTooltip?: string;
  // Extra block under the onboarding breakdown on every pill (e.g. where an invite puts
  // the person). It may hold buttons, so the card stays open while hovered.
  tooltipExtra?: React.ReactNode;
}

type ChipState = 'complete' | 'incomplete' | 'neutral';

// The tooltip card is dark in both themes, so its internals use the dark-mode
// palette tokens regardless of the active theme
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tooltip: {
      backgroundColor: theme.palette.type === 'dark' ? monochrome.darker : monochrome.darkest,
      borderRadius: 12,
      padding: theme.spacing(2),
      maxWidth: 320,
      minWidth: 220,
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gridGap: 6,
      padding: '2px 10px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
    },
    dot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      flexShrink: 0,
    },
    progressTrack: {
      flex: 1,
      height: 6,
      borderRadius: 3,
      backgroundColor: alpha(monochrome.lightest, 0.24),
      overflow: 'hidden',
    },
    progressBar: {
      height: '100%',
      borderRadius: 3,
    },
    extra: {
      marginTop: theme.spacing(1.5),
      paddingTop: theme.spacing(1.5),
      borderTop: `1px solid ${alpha(monochrome.lightest, 0.16)}`,
    },
  })
);

const getModuleState = (status?: OnboardingStatusBase | null): ChipState => {
  if (status?.is_complete) {
    return 'complete';
  }
  if (typeof getModuleCompletionPercent(status ?? {}) === 'number') {
    return 'incomplete';
  }
  return 'neutral';
};

const normalizeModules = (modules?: string | string[] | null): string[] => {
  if (!modules) {
    return [];
  }
  const list = Array.isArray(modules) ? modules : modules.split(',');
  return list.map(module => module.trim()).filter(Boolean);
};

const OnboardingTooltipContent = ({
  label,
  state,
  status,
  emptyTooltip,
}: {
  label: string;
  state: ChipState;
  status?: OnboardingStatusBase | null;
  emptyTooltip: string;
}) => {
  const classes = useStyles();
  const stateColor =
    state === 'complete'
      ? storiesDarkModeColor.success
      : state === 'incomplete'
      ? storiesDarkModeColor.warning
      : monochrome.medium;
  const percent = state === 'complete' ? 100 : getModuleCompletionPercent(status ?? {});
  const missing = (status?.missing_required ?? [])
    .map(item => item.label || item.key)
    .filter(Boolean) as string[];

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={24}>
        <Typography variant="subtitle2" style={{ color: monochrome.lightest, fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="subtitle2" style={{ color: stateColor, fontWeight: 600 }}>
          {state === 'complete' ? 'Complete' : state === 'incomplete' ? 'Incomplete' : ''}
        </Typography>
      </Box>

      {typeof percent === 'number' ? (
        <Box display="flex" alignItems="center" gridGap={8}>
          <Box className={classes.progressTrack}>
            <Box className={classes.progressBar} style={{ width: `${percent}%`, backgroundColor: stateColor }} />
          </Box>
          <Typography variant="caption" style={{ color: monochrome.lightest, fontWeight: 700 }}>
            {percent}%
          </Typography>
        </Box>
      ) : null}

      {state === 'complete' && (
        <Typography variant="caption" style={{ color: stateColor }}>
          All mandatory data points collected
        </Typography>
      )}

      {state === 'incomplete' && missing.length > 0 && (
        <Box display="flex" flexDirection="column" gridGap={4}>
          <Typography variant="caption" style={{ color: monochrome.medium, fontWeight: 600 }}>
            {`MISSING ${missing.length} MANDATORY DATA POINT${missing.length === 1 ? '' : 'S'}`}
          </Typography>
          {missing.map(item => (
            <Box key={item} display="flex" alignItems="center" gridGap={8}>
              <Box className={classes.dot} style={{ backgroundColor: stateColor }} />
              <Typography variant="caption" style={{ color: monochrome.lightest }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {state === 'neutral' && (
        <Typography variant="caption" style={{ color: monochrome.medium }}>
          {emptyTooltip}
        </Typography>
      )}
    </Box>
  );
};

interface OnboardingChipProps {
  label: string;
  status?: OnboardingStatusBase | null;
  emptyTooltip?: string;
  // Modules outside the onboarding rulesets (e.g. Projects) get a plain chip
  withTooltip?: boolean;
  tooltipExtra?: React.ReactNode;
}

// One pill tinted by an onboarding state, with the breakdown (percentage and the
// missing mandatory fields) on hover. Unknown is not complete — no computed status
// stays neutral, never green and never 0%.
export const OnboardingChip: React.FC<OnboardingChipProps> = ({
  label,
  status,
  emptyTooltip = 'Not assessed yet',
  withTooltip = true,
  tooltipExtra,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const state = getModuleState(status);
  const color =
    state === 'complete'
      ? theme.palette.success.main
      : state === 'incomplete'
      ? theme.palette.warning.main
      : theme.palette.text.secondary;

  const chip = (
    <Box
      className={classes.chip}
      style={{
        backgroundColor: alpha(color, theme.palette.type === 'dark' ? 0.16 : 0.1),
        color,
      }}
    >
      <Box className={classes.dot} style={{ backgroundColor: color }} />
      <Typography variant="caption" style={{ fontWeight: 600, color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );

  if (!withTooltip) {
    return chip;
  }

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      interactive={!!tooltipExtra}
      title={
        <>
          <OnboardingTooltipContent
            label={label}
            state={state}
            status={status}
            emptyTooltip={emptyTooltip}
          />
          {tooltipExtra && <Box className={classes.extra}>{tooltipExtra}</Box>}
        </>
      }
    >
      {chip}
    </Tooltip>
  );
};

// Consolidated "Modules + Onboarding" cell: one pill per module the person is in,
// tinted by that module's onboarding state.
export const ModuleOnboardingChips: React.FC<Props> = ({
  modules,
  onboarding,
  emptyTooltip,
  tooltipExtra,
}) => {
  const theme = useTheme();
  const moduleNames = normalizeModules(modules);

  if (!moduleNames.length) {
    return (
      <Box color={theme.palette.text.secondary}>
        <Typography variant="subtitle2">-</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexWrap="wrap" gridGap={4}>
      {moduleNames.map(name => {
        const key = getOnboardingModuleKeyByName(name);
        return (
          <OnboardingChip
            key={name}
            label={name}
            status={key ? onboarding?.[key] : undefined}
            emptyTooltip={emptyTooltip}
            withTooltip={!!key}
            tooltipExtra={tooltipExtra}
          />
        );
      })}
    </Box>
  );
};
